import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CheckCheck, ChevronDown, Loader2, MessageCircle, Upload, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { formatDate } from '@/shared/lib/dateUtils'
import { nombreDelMes } from '@/features/proveedores/lib/mesesDelPaquete'
import { EsqueletoTabla } from '@/shared/components/Esqueleto'
import {
  paqueteMensualService,
  type CasillaDelPaquete,
  type EstadoPaquete,
  type PaqueteDelHistorial,
  type PaqueteMensual,
} from '@/features/proveedores/api/paqueteMensual.service'
import { Badge, type ColorDeBadge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'


/**
 * Texto del banner por estado. Los del proveedor son los de C-07; Contraloría
 * ve el mismo estado contado desde su lado —"tus documentos" no le dice nada a
 * quien está revisando los de alguien más—.
 */
const COLORES_BANNER: Record<EstadoPaquete, string> = {
  pending: 'bg-[#FEF9EC] text-[#B0740A] border-[#F5E3B8]',
  in_review: 'bg-[#EEF3FC] text-[#2563EB] border-[#CBDAF6]',
  approved: 'bg-[#EBFCF1] text-[#0F9D58] border-[#BFEACF]',
  rejected: 'bg-[#FBEEEE] text-[#EF4444] border-[#F5C6CB]',
}

const TEXTO_BANNER: Record<'proveedor' | 'contraloria', Record<EstadoPaquete, string>> = {
  proveedor: {
    pending: 'Sube tus 14 documentos para poder facturar este mes.',
    in_review: 'Tus documentos están siendo revisados por Contraloría.',
    approved: 'Paquete aprobado. Ya puedes facturar.',
    rejected: 'Tu paquete tiene documentos rechazados. Corrígelos y vuelve a enviar.',
  },
  contraloria: {
    pending: 'El proveedor todavía no ha enviado su paquete a revisión.',
    in_review: 'Paquete enviado a revisión. Aprueba o rechaza cada documento.',
    approved: 'Paquete aprobado. El proveedor ya puede facturar este mes.',
    rejected: 'Paquete rechazado. El proveedor debe corregir y volver a enviarlo.',
  },
}

/** Estatus que se muestra por documento. */
const estatusDeCasilla = (casilla: CasillaDelPaquete): { etiqueta: string; color: ColorDeBadge } => {
  const doc = casilla.document
  if (!doc?.fileUrl) return { etiqueta: 'No subido', color: 'gris' }
  if (doc.status === 'approved') return { etiqueta: 'Aprobado', color: 'verde' }
  if (doc.status === 'rejected') return { etiqueta: 'Rechazado', color: 'rojo' }
  return { etiqueta: 'Subido', color: 'azul' }
}

interface PaqueteMensualTabProps {
  /**
   * `proveedor`: sube sus documentos y los envía a revisión.
   * `contraloria`: aprueba o rechaza cada documento.
   */
  modo: 'proveedor' | 'contraloria'
  /** Solo en modo contraloría: de quién es el paquete. */
  supplierId?: string
}

const PaqueteMensualTab = ({ modo, supplierId }: PaqueteMensualTabProps) => {
  const [paquete, setPaquete] = useState<PaqueteMensual | null>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [subiendo, setSubiendo] = useState<number | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [historial, setHistorial] = useState<PaqueteDelHistorial[]>([])
  // Fechas de vencimiento que Contraloria corrige antes de aprobar o rechazar,
  // por catalogDocId. Se limpian al releer el paquete.
  const [fechas, setFechas] = useState<Record<number, string>>({})
  const [verHistorial, setVerHistorial] = useState(false)
  const [comentarioVisible, setComentarioVisible] = useState<CasillaDelPaquete | null>(null)
  const [rechazando, setRechazando] = useState<CasillaDelPaquete | null>(null)
  const [comentarioRechazo, setComentarioRechazo] = useState('')
  const inputsPorCasilla = useRef<Record<number, HTMLInputElement | null>>({})

  const esProveedor = modo === 'proveedor'

  const cargar = useCallback(async () => {
    setCargando(true)
    setError(null)
    try {
      const datos = esProveedor
        ? await paqueteMensualService.miPaquete()
        : await paqueteMensualService.paqueteDeProveedor(supplierId!)
      setPaquete(datos)
      // Lo capturado ya viene en la respuesta; conservarlo dejaria fechas
      // fantasma en filas que ya se revisaron.
      setFechas({})
    } catch {
      setError('No se pudo cargar el paquete del mes.')
    } finally {
      setCargando(false)
    }
  }, [esProveedor, supplierId])

  useEffect(() => {
    if (!esProveedor && !supplierId) return
    cargar()
  }, [cargar, esProveedor, supplierId])

  useEffect(() => {
    if (!esProveedor) return
    paqueteMensualService.miHistorial().then(setHistorial).catch(() => setHistorial([]))
  }, [esProveedor])

  // En su propio useMemo: como arreglo nuevo en cada render hacia que el useMemo
  // de abajo se recalculara siempre, y el lint lo marca con razon.
  const casillas = useMemo(() => paquete?.documents ?? [], [paquete])
  const subidos = useMemo(
    () => casillas.filter((c) => c.document?.fileUrl).length,
    [casillas],
  )
  const estado = paquete?.status ?? 'pending'
  // El paquete del mes lo crea el cron J-03 el dia 1. Mientras no existe, el
  // back devuelve exists:false con las 14 casillas vacias, y cualquier subida
  // responde 404. Sin esta bandera la pantalla ofrecia Subir en las 14 filas y
  // la subida fallaba con un mensaje tecnico en ingles, que se leia como que
  // no pasaba nada.
  const paqueteAbierto = paquete?.exists === true
  // El proveedor solo puede tocar sus documentos mientras el paquete no se haya
  // enviado: en revisión y aprobado son de solo lectura, como pide C-07.
  const puedeSubir =
    esProveedor && paqueteAbierto && (estado === 'pending' || estado === 'rejected')
  const puedeEnviar = puedeSubir && subidos === casillas.length && casillas.length > 0

  const subir = async (catalogDocId: number, archivo: File) => {
    if (archivo.type !== 'application/pdf') {
      toast.error('El documento debe ser un PDF.')
      return
    }
    setSubiendo(catalogDocId)
    try {
      await paqueteMensualService.subirDocumento(catalogDocId, archivo)
      await cargar()
      toast.success('Documento subido')
    } catch (err: unknown) {
      const e = err as { message?: string | string[]; statusCode?: number }
      const mensaje = Array.isArray(e.message) ? e.message.join(' ') : e.message
      // El mensaje del back se muestra tal cual: el generico que estaba aqui
      // tapaba el motivo real —por ejemplo que el expediente no aplica a este
      // proveedor— y se leia como que la subida no hacia nada.
      toast.error(mensaje || 'No se pudo subir el documento')
    } finally {
      setSubiendo(null)
    }
  }

  const enviarARevision = async () => {
    setEnviando(true)
    try {
      await paqueteMensualService.enviarARevision()
      await cargar()
      toast.success('Paquete enviado a revisión')
    } catch (err: unknown) {
      const e = err as { message?: string | string[] }
      const mensaje = Array.isArray(e.message) ? e.message.join(' ') : e.message
      toast.error(mensaje || 'No se pudo enviar a revisión')
    } finally {
      setEnviando(false)
    }
  }

  /**
   * Contraloria puede corregir la fecha de vencimiento mientras el documento
   * sigue sin revisar. Despues no: el PATCH exige un status y reenviarlo
   * borraria el comentario del rechazo, asi que la fila queda cerrada igual
   * que sus botones.
   */
  const puedeCorregirFecha = (doc: CasillaDelPaquete['document']) =>
    !esProveedor && !!doc?.fileUrl && doc.status === 'pending'

  const revisar = async (
    casilla: CasillaDelPaquete,
    status: 'approved' | 'rejected',
    comentario?: string,
  ) => {
    if (!casilla.document) return
    try {
      // La fecha viaja junto con la revision: el PATCH exige un status, asi que
      // no hay forma de guardarla sola sin aprobar o rechazar el documento.
      await paqueteMensualService.revisarDocumento(
        casilla.document.id,
        status,
        comentario,
        fechas[casilla.catalogDocId] || undefined,
      )
      // El back recalcula el estado del paquete despues de cada accion, asi que
      // se relee completo en vez de parchear la fila.
      await cargar()
      toast.success(status === 'approved' ? 'Documento aprobado' : 'Documento rechazado')
    } catch (err: unknown) {
      const e = err as { message?: string | string[] }
      const mensaje = Array.isArray(e.message) ? e.message.join(' ') : e.message
      toast.error(mensaje || 'No se pudo revisar el documento')
    }
  }

  // A-01: silueta de la tabla de las 14 casillas.
  if (cargando) {
    return (
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        <EsqueletoTabla filas={8} columnas={6} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-gray-500">{error}</p>
        <button
          onClick={cargar}
          className={estiloDeBoton('primario', 'mt-3')}
        >
          Reintentar
        </button>
      </div>
    )
  }

  // Sin paquete hay dos motivos posibles y el aviso anterior solo contaba uno
  // —culpaba al cron del dia 1—, asi que a un proveedor General o Fletes le
  // decia que esperara algo que nunca le va a tocar. El expediente mensual solo
  // aplica a proveedores de Servicios aprobados.
  const banner = paqueteAbierto
    ? { texto: TEXTO_BANNER[modo][estado], clases: COLORES_BANNER[estado] }
    : {
        texto:
          modo === 'proveedor'
            ? 'Todavía no tienes expediente mensual para este mes. Solo aplica a proveedores de Servicios aprobados; si es tu caso, avisa a Compras.'
            : 'Este proveedor no tiene expediente para el mes. Solo se genera para proveedores de Servicios aprobados.',
        clases: COLORES_BANNER.pending,
      }

  return (
    <div>
      {/* Encabezado: mes y estado */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Documentos de {nombreDelMes(paquete?.month ?? 0, paquete?.year ?? 0)}
          </h3>
          {paqueteAbierto && (
            <p className="text-sm text-gray-500 mt-0.5">
              {subidos} de {casillas.length} documentos subidos
            </p>
          )}
        </div>
        {puedeSubir && (
          <button
            onClick={enviarARevision}
            disabled={!puedeEnviar || enviando}
            title={puedeEnviar ? undefined : 'Sube los 14 documentos para poder enviar'}
            className={estiloDeBoton('primario')}
          >
            {enviando && <Loader2 className="w-4 h-4 girando" />}
            Enviar a revisión
          </button>
        )}
      </div>

      <div className={`border rounded-lg px-4 py-3 mb-5 text-sm font-medium ${banner.clases}`}>
        {banner.texto}
      </div>

      {/* Tabla de las 14 casillas. El catálogo viene del back, no se escribe aquí.
          Sin paquete no se pinta: el back devuelve igual las 14 casillas vacias,
          asi que la tabla se veia identica a un expediente abierto en cero, con
          las filas en No subido y sin manera de subir nada ni de saber por que.
          Eso fue lo que se reporto como una falla en silencio. */}
      {paqueteAbierto ? (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FAF9FF] border-b border-gray-200">
                {/* Dos columnas se llaman Documento, como en el diseño: la primera es el
                    nombre del catalogo y la cuarta el archivo subido. */}
                {['Documento', 'Fecha de subida', 'Fecha de vencimiento', 'Documento', 'Estatus', 'Acciones'].map((h, i) => (
                  <th key={`${h}-${i}`} className="text-left px-4 py-3 text-sm font-medium text-[#1B2128] whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {casillas.map((casilla) => {
                const doc = casilla.document
                const estatus = estatusDeCasilla(casilla)
                const enProceso = subiendo === casilla.catalogDocId
                return (
                  <tr key={casilla.catalogDocId} className="border-b border-gray-100 last:border-0">
                    <td className="px-4 py-3 text-gray-800">
                      {casilla.catalogDocId}. {casilla.name}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {doc?.uploadedAt ? formatDate(doc.uploadedAt) : '——————'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {puedeCorregirFecha(doc) ? (
                        <input
                          type="date"
                          value={fechas[casilla.catalogDocId] ?? doc!.validUntil?.slice(0, 10) ?? ''}
                          onChange={(e) =>
                            setFechas((previas) => ({
                              ...previas,
                              [casilla.catalogDocId]: e.target.value,
                            }))
                          }
                          className="px-2 py-1 rounded-lg border border-[#E0E0E0] text-xs text-[#534D59] focus:outline-none focus:border-[#2563EB]"
                        />
                      ) : (
                        (doc?.validUntil ? formatDate(doc.validUntil) : '——————')
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {doc?.fileUrl ? (
                        <a
                          href={doc.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#2563EB] hover:underline break-all"
                        >
                          {doc.fileName ?? 'Ver documento'}
                        </a>
                      ) : (
                        <span className="text-gray-400">Sin archivo</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge color={estatus.color}>{estatus.etiqueta}</Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap align-top">
                      <div className="flex flex-col items-start">
                      {/* Proveedor: subir o reemplazar mientras el paquete esté abierto */}
                      {puedeSubir && (
                        <>
                          <input
                            ref={(el) => { inputsPorCasilla.current[casilla.catalogDocId] = el }}
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={(e) => {
                              const archivo = e.target.files?.[0]
                              e.target.value = ''
                              if (archivo) subir(casilla.catalogDocId, archivo)
                            }}
                          />
                          <button
                            onClick={() => inputsPorCasilla.current[casilla.catalogDocId]?.click()}
                            disabled={enProceso}
                            className={estiloDeBoton('primario')}
                          >
                            {enProceso ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            {doc?.fileUrl ? 'Reemplazar' : 'Subir'}
                          </button>
                        </>
                      )}

                      {/* Contraloría: aprobar o rechazar cada documento subido.
                          Un documento ya revisado no se vuelve a aprobar ni a
                          rechazar: los dos botones se bloquean, no solo el que
                          corresponde al estado actual. Para deshacer una
                          revision hay que resolverlo por otro camino, y dejar
                          los botones vivos sugeria que se podia ir y venir.
                          En un documento rechazado el diseno no los pinta
                          siquiera: ahi la unica accion es leer el motivo. */}
                      {!esProveedor && doc?.status !== 'rejected' && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => revisar(casilla, 'approved')}
                            disabled={!doc?.fileUrl || doc.status !== 'pending'}
                            className={estiloDeBoton('exito')}
                          >
                            Aprobar
                            <CheckCheck className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => { setRechazando(casilla); setComentarioRechazo('') }}
                            disabled={!doc?.fileUrl || doc.status !== 'pending'}
                            className={estiloDeBoton('peligro')}
                          >
                            Rechazar
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}

                      {/* El motivo del rechazo se ve en los dos modos. Va en su
                          propio renglon con separacion: pegado al boton de
                          reemplazar parecia parte de el. */}
                      {doc?.status === 'rejected' && doc.reviewComment && (
                        <button
                          onClick={() => setComentarioVisible(casilla)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2563EB] hover:underline mt-2.5"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          Ver comentario
                        </button>
                      )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-8 text-center">
          <p className="text-sm text-gray-500">
            Cuando el expediente del mes esté disponible, aquí aparecen los{' '}
            {casillas.length} documentos que se piden.
          </p>
        </div>
      )}

      {/* Meses anteriores — solo en el portal, como pide C-07 */}
      {esProveedor && historial.length > 0 && (
        <div className="mt-5">
          <button
            onClick={() => setVerHistorial((v) => !v)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${verHistorial ? 'rotate-180' : ''}`} />
            Meses anteriores ({historial.length})
          </button>
          {verHistorial && (
            <div className="mt-3 bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
              {historial.map((mes) => (
                <div key={`${mes.year}-${mes.month}`} className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm text-gray-800 capitalize">
                    {nombreDelMes(mes.month, mes.year)}
                  </span>
                  <Badge color={
                    mes.status === 'approved' ? 'verde'
                      : mes.status === 'rejected' ? 'rojo'
                      : mes.status === 'in_review' ? 'azul'
                      : 'amarillo'
                  }>
                    {mes.status === 'approved' ? 'Aprobado'
                      : mes.status === 'rejected' ? 'Rechazado'
                      : mes.status === 'in_review' ? 'En revisión'
                      : 'Pendiente'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Comentario de rechazo */}
      {comentarioVisible && (
        <CapaDeModal onClickFondo={() => setComentarioVisible(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {comentarioVisible.name}
            </h3>
            <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap">
              {comentarioVisible.document?.reviewComment}
            </p>
            <div className="flex justify-end mt-5">
              <button
                onClick={() => setComentarioVisible(null)}
                className={estiloDeBoton('secundario')}
              >
                Cerrar
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Rechazo con motivo: sin comentario el proveedor no sabe qué corregir */}
      {rechazando && (
        <CapaDeModal onClickFondo={() => setRechazando(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900">Rechazar documento</h3>
            <p className="text-sm text-gray-500 mt-0.5">{rechazando.name}</p>
            <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
              Motivo del rechazo
            </label>
            <textarea
              rows={4}
              value={comentarioRechazo}
              onChange={(e) => setComentarioRechazo(e.target.value)}
              placeholder="Explica qué tiene que corregir el proveedor"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
            />
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setRechazando(null)}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  const casilla = rechazando
                  setRechazando(null)
                  await revisar(casilla, 'rejected', comentarioRechazo.trim() || undefined)
                }}
                disabled={!comentarioRechazo.trim()}
                title={comentarioRechazo.trim() ? undefined : 'Escribe el motivo'}
                className={estiloDeBoton('peligro')}
              >
                Rechazar documento
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}
    </div>
  )
}

export default PaqueteMensualTab
